/* OneVine: the Mass page renderer.
   One full-screen WebGL canvas behind the words. Each frame it draws:
     1. a background: a painting seen through a moving camera under a raking light, or the road to
        Emmaus at a given hour (sky, hills, road, milestones, stars);
     2. a second painting seen through a growing circle (the "aperture"), which is how one scene
        passes into the next;
     3. one white circle that is by turns the moon, the sun, the bread and the host.
   Nothing here decides what to draw: assets/onevine-mass.js hands draw() a finished state. */
(function () {
  'use strict';

  var VERT = 'attribute vec2 aPos;void main(){gl_Position=vec4(aPos,0.,1.);}';

  var FRAG = [
    'precision highp float;',
    'uniform vec2 uRes;',          // drawing buffer, device px
    'uniform float uPx;',          // device px per css px
    'uniform sampler2D uTexA;',
    'uniform sampler2D uTexB;',
    'uniform vec4 uCamA;',         // uv of the screen's top-left corner, uv per device px (x, y)
    'uniform vec4 uCamB;',
    'uniform float uHasA;',        // texture ready, 0..1
    'uniform float uHasB;',
    'uniform vec4 uAp;',           // aperture: centre x, y, radius (device px, y down), rim strength
    'uniform float uFade;',        // plain crossfade A -> B
    'uniform float uMode;',        // 0 painting, 1 road
    'uniform vec4 uLight;',        // spotlight centre x, y, radius (device px), ambient
    'uniform float uRelief;',
    'uniform float uBlur;',        // mipmap bias: a soft, out-of-focus painting (WebGL2 only)
    'uniform vec4 uDisc;',         // centre x, y, radius (device px), opacity
    'uniform vec4 uDiscLook;',     // (unused), glow, break, (unused)
    'uniform vec3 uKind;',         // how much moon, sun and host the circle is (sums to 1)
    'uniform vec4 uScrim;',        // left, right, bottom, top
    'uniform vec2 uTone;',         // exposure, dim toward ink
    'uniform vec3 uSkyTop;',
    'uniform vec3 uSkyMid;',
    'uniform vec3 uSkyLow;',
    'uniform vec4 uRoad;',         // horizon (0..1 from top), travel, night, city lights
    'uniform vec2 uRoadView;',     // vanishing point x (0..1), bend',
    'const vec3 INK = vec3(.043,.039,.035);',
    'const vec3 GOLD = vec3(.78,.63,.36);',
    'float hash(vec2 p){vec3 q=fract(vec3(p.xyx)*.1031);q+=dot(q,q.yzx+33.33);return fract((q.x+q.y)*q.z);}',  // no sin(): exact on every GPU
    'float noise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);',
    '  return mix(mix(hash(i),hash(i+vec2(1.,0.)),f.x),mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),f.x),f.y);}',
    'float lum(vec3 c){return dot(c,vec3(.299,.587,.114));}',
    'float fbm(vec2 p){float v=0.,a=.5;mat2 m=mat2(1.6,1.2,-1.2,1.6);for(int i=0;i<4;i++){v+=a*noise(p);p=m*p;a*=.5;}return v;}',

    // A painting, lit. uv is y-down (the picture's top row is v = 0).
    'vec3 painting(sampler2D t,vec4 cam,vec2 p,float has){',
    '  vec2 uv=cam.xy+p*cam.zw;',
    '  vec2 e=vec2(abs(cam.z),abs(cam.w))*1.25;',
    '  vec3 c=texture2D(t,uv,uBlur).rgb;',
    '  float gx=0.,gy=0.;',
    '  if(uRelief>0.){gx=lum(texture2D(t,uv+vec2(e.x,0.)).rgb)-lum(texture2D(t,uv-vec2(e.x,0.)).rgb);',
    '    gy=lum(texture2D(t,uv+vec2(0.,e.y)).rgb)-lum(texture2D(t,uv-vec2(0.,e.y)).rgb);}',
    '  vec3 n=normalize(vec3(-gx*uRelief*7.,-gy*uRelief*7.,1.));',
    '  vec3 L=normalize(vec3((uLight.xy-p)/uRes.y,.55));',
    '  float diff=dot(n,L);',
    '  float d=length(p-uLight.xy)/max(uLight.z,1.);',
    '  float spot=exp(-d*d*1.6);',
    '  float lit=mix(uLight.w,1.,spot);',
    '  c=c*lit*(.86+.3*diff)+vec3(1.,.93,.8)*pow(max(diff,0.),12.)*.05*spot*uRelief;',
    '  float edge=smoothstep(0.,.03,uv.x)*smoothstep(1.,.97,uv.x)*smoothstep(0.,.03,uv.y)*smoothstep(1.,.97,uv.y);',
    '  return mix(INK,c,edge*has);',
    '}',

    // The circle: moon (0), sun (1), host (2). q is the position in units of its radius.
    'vec3 discColor(vec2 q,vec3 w){',
    '  float d=length(q);',
    '  vec3 moon=vec3(.88,.91,.97);',
    '  if(w.x>.001){moon*=.93+.07*fbm(q*6.+7.);moon*=1.-.17*smoothstep(.5,.68,fbm(q*1.7+vec2(3.1,1.3)));moon*=.8+.2*sqrt(max(0.,1.-d*d));}',
    '  vec3 sun=mix(vec3(1.,.98,.9),vec3(1.,.83,.52),smoothstep(.35,1.,d));',
    '  float cr=max((1.-smoothstep(.03,.045,abs(q.x)))*(1.-smoothstep(.5,.54,abs(q.y))),(1.-smoothstep(.03,.045,abs(q.y)))*(1.-smoothstep(.5,.54,abs(q.x))));',
    '  vec3 host=vec3(.98,.96,.91)*(1.-.07*smoothstep(.84,1.,d))-.045*cr+.02*(noise(q*9.)-.5);',
    '  return (moon*w.x+sun*w.y+host*w.z)/max(w.x+w.y+w.z,.001);',
    '}',
    'vec3 glowColor(vec3 w){return (vec3(.62,.72,.95)*w.x+vec3(1.,.62,.3)*w.y+vec3(1.,.9,.7)*w.z)/max(w.x+w.y+w.z,.001);}',
    // returns colour (rgb) and coverage (a); the break pulls two halves apart along a ragged line
    'vec4 disc(vec2 p,out float glow){',
    '  glow=0.;',
    '  if(uDisc.w<=0.001||uDisc.z<=.5)return vec4(0.);',
    '  vec2 q=(p-uDisc.xy)/uDisc.z;',
    '  float soft=1.6/uDisc.z;',
    '  float b=uDiscLook.z;',
    '  float cover=0.;vec3 col=vec3(0.);',
    '  if(b<=.001){',
    '    float d=length(q);cover=1.-smoothstep(1.-soft,1.+soft,d);if(cover>0.)col=discColor(q,uKind);',
    '    glow=exp(-max(d-1.,0.)*2.6)*uDiscLook.y;',
    '  }else{',
    '    float gap=b*.42;float ang=b*.22;',
    '    for(int s=0;s<2;s++){',
    '      float side=s==0?-1.:1.;',
    '      vec2 r=q-vec2(side*gap,b*.12);',
    '      float ca=cos(side*ang),sa=sin(side*ang);r=vec2(ca*r.x-sa*r.y,sa*r.x+ca*r.y);',
    '      float crack=.06*(noise(vec2(r.y*7.,1.))-.5)+.03*(noise(vec2(r.y*19.,4.))-.5);',
    '      float inside=side<0.?smoothstep(soft,-soft,r.x-crack):smoothstep(-soft,soft,r.x-crack);',
    '      float d=length(r);float c=(1.-smoothstep(1.-soft,1.+soft,d))*inside;',
    '      if(c>0.)col+=discColor(r,uKind)*c;cover+=c;',
    '      glow+=exp(-max(d-1.,0.)*2.6)*uDiscLook.y*.6;',
    '    }',
    '    col/=max(cover,.001);cover=min(cover,1.);',
    '  }',
    '  return vec4(col,cover*uDisc.w);',
    '}',

    // The road to Emmaus. s is the screen position 0..1 (y down).
    'float ridge(float x,float seed){return .55*noise(vec2(x*1.3+seed,seed))+.3*noise(vec2(x*3.1+seed*2.,seed))+.15*noise(vec2(x*7.7,seed+5.));}',
    'vec3 road(vec2 p,vec2 s,vec4 dk,float dglow){',
    '  float hz=uRoad.x,night=uRoad.z;',
    '  float aspect=uRes.x/uRes.y;',
    '  float t=clamp(s.y/hz,0.,1.);',
    '  vec3 col=t<.62?mix(uSkyTop,uSkyMid,t/.62):mix(uSkyMid,uSkyLow,(t-.62)/.38);',
    // stars
    '  vec2 sg=p/(14.*uPx);vec2 cell=floor(sg);float h=hash(cell);',
    '  if(h>.965&&s.y<hz*.92){float sd=length(fract(sg)-.5-vec2(hash(cell+1.)-.5,hash(cell+2.)-.5)*.5);',
    '    col+=vec3(.9,.92,1.)*(1.-smoothstep(.02,.09,sd))*night*(.4+.6*hash(cell+3.));}',
    // the sun or moon sits in the sky, behind the hills
    '  vec3 gc=glowColor(uKind);col+=gc*dglow*.55*uDisc.w;',
    '  col=mix(col,dk.rgb,dk.a);',
    // far hills, and nearer hills that rise toward the sides so the road runs down a valley
    '  float x=s.x*aspect;float tr=uRoad.y;float dx=abs(s.x-uRoadView.x);',
    '  float h1=hz-.014-.09*ridge(x*.7+tr*.003,3.);',
    '  float h2=hz+.003-(.012+.24*clamp(aspect,.45,1.)*pow(min(dx*1.7,1.),1.8))*(.55+.45*(.65*noise(vec2(x*1.95+tr*.014+11.,11.))+.35*noise(vec2(x*4.6+tr*.03,13.))));',
    '  vec3 haze=mix(uSkyLow,uSkyMid,.3);',
    '  vec3 c1=mix(haze,INK,.42+.3*night);vec3 c2=mix(mix(uSkyLow,vec3(.16,.12,.1),.6),INK,.55+.3*night);',
    '  float in1=smoothstep(h1-.0015,h1+.0015,s.y);col=mix(col,c1,in1);',
    '  col+=glowColor(uKind)*smoothstep(h1-.002,h1+.002,s.y)*(1.-smoothstep(h1+.002,h1+.02,s.y))*.12*(1.-night)*uDisc.w;',
    '  float in2=smoothstep(h2-.0015,h2+.0015,s.y);col=mix(col,c2*(1.-.25*smoothstep(h2,hz,s.y)),in2);',
    '  col+=glowColor(uKind)*smoothstep(h2-.002,h2+.002,s.y)*(1.-smoothstep(h2+.002,h2+.016,s.y))*.1*(1.-night)*uDisc.w;',
    // Jerusalem's lamps along the far hill, near the end of the road (the walk back)
    '  if(uRoad.w>0.){vec2 lc=floor(p/(3.*uPx));float lh=hash(lc+9.);',
    '    float band=(1.-smoothstep(.03,.11,abs(s.x-uRoadView.x)))*step(h1+.002,s.y)*step(s.y,hz-.001);',
    '    vec2 lf=fract(p/(3.*uPx))-.5;',
    '    col+=vec3(1.,.74,.42)*step(.93,lh)*band*uRoad.w*(1.-smoothstep(.15,.45,length(lf)))*(.6+.4*hash(lc+2.));}',
    // the plain and the road: a pinhole camera 0.7 units above flat ground; 1 unit = half the road's width
    '  if(s.y>hz){',
    '    float dz=max(s.y-hz,.0006);float z=.7/dz;',
    '    float wx=(s.x-uRoadView.x)*aspect*z;',
    '    float bend=uRoadView.y*z*z*.002;',
    '    float rx=abs(wx-bend);',
    '    vec3 dirt=mix(mix(uSkyLow,vec3(.36,.27,.18),.55),vec3(.1,.09,.12),night*.8);',
    '    vec3 grass=mix(mix(uSkyLow,vec3(.12,.12,.07),.78),vec3(.03,.035,.05),night*.85);',
    '    float far=smoothstep(.0,.05,dz);',
    '    float n=mix(.5,noise(vec2(wx*1.6,(z+tr)*.35))*.6+noise(vec2(wx*5.,(z+tr)*1.2))*.4,far);',
    '    float edge=smoothstep(1.06,.94,rx+.1*(n-.5));',
    '    vec3 g=mix(grass*(.75+.4*n),dirt*(.82+.3*n),edge);',
    '    float rut=smoothstep(.06,.0,abs(rx-.42))*.14*edge*far;g*=1.-rut;',
    '    g*=mix(.55,1.15,smoothstep(.3,0.,dz))*(1.-.45*night);',
    '    float fog=smoothstep(.08,0.,dz);g=mix(g,mix(uSkyLow*.85,c2,.45),fog*.65);',
    '    col=g;',
    // milestones: Roman stones on the verges, one every 9 units of travel, sides taking turns
    '    if(s.y<hz+.19&&abs(s.x-uRoadView.x)<.55)for(int k=0;k<5;k++){',
    '      float idx=floor(tr/9.)+float(k)+1.;',
    '      float zm=idx*9.-tr;',
    '      float side=mod(idx,2.)<.5?1.:-1.;',
    '      float sy0=hz+.7/zm;float hgt=.36/zm;',
    '      float sx0=uRoadView.x+(side*1.45+uRoadView.y*zm*zm*.002)/(aspect*zm);',
    '      float w=.075/(aspect*zm);',
    '      float ux=(s.x-sx0)/w;float uy=(sy0-s.y)/hgt;',
    '      float body=step(abs(ux),1.)*step(0.,uy)*step(uy,1.);',
    '      float cap=1.-smoothstep(.75,1.,length(vec2(ux,(uy-.82)/.3)))*step(.82,uy);',
    '      float shape=body*cap*(1.-smoothstep(.85,1.,abs(ux)));',
    '      float shade=.55+.45*clamp(.5-ux*.5*side,0.,1.);',
    '      vec3 stone=mix(vec3(.5,.45,.38),vec3(.1,.1,.13),night*.85)*shade*mix(1.,.7,smoothstep(0.,.2,uy)*0.);',
    '      float seen=smoothstep(4.,9.,zm)*(1.-fog);',
    '      float shadow=(1.-smoothstep(0.,1.,length(vec2(ux*.55,(s.y-sy0)/(hgt*.12)))))*.45*seen;',
    '      col*=1.-shadow*(1.-night);',
    '      col=mix(col,stone,shape*seen);',
    '    }',
    '  }',
    '  return col;',
    '}',

    'void main(){',
    '  vec2 p=vec2(gl_FragCoord.x,uRes.y-gl_FragCoord.y);',
    '  vec2 s=p/uRes;',
    '  float dglow;vec4 dk=disc(p,dglow);',
    '  vec3 col;',
    '  if(uMode>.5){col=road(p,s,dk,dglow);}',
    '  else{col=painting(uTexA,uCamA,p,uHasA);}',
    '  if(uFade>0.){col=mix(col,painting(uTexB,uCamB,p,uHasB),uFade);}',
    '  if(uAp.z>0.){',
    '    float d=length(p-uAp.xy)-uAp.z;',
    '    float inside=1.-smoothstep(-1.5*uPx,1.5*uPx,d);',
    '    if(inside>0.)col=mix(col,painting(uTexB,uCamB,p,uHasB),inside);',
    '    col+=GOLD*exp(-abs(d)/(7.*uPx))*uAp.w*.9+vec3(1.,.92,.75)*exp(-abs(d)/(2.*uPx))*uAp.w*.5;',
    '  }',
    // the scene's exposure and dimming darken the picture, not the circle: it is the light in the scene
    '  col*=uTone.x;',
    '  col=mix(col,INK,uTone.y);',
    '  if(uMode<=.5){',
    '    col+=glowColor(uKind)*dglow*.42*uDisc.w;',
    '    col=mix(col,dk.rgb,dk.a);',
    '  }',
    // scrims behind the words, a soft vignette, a little grain
    '  float sc=uScrim.x*(1.-smoothstep(.34,.72,s.x))+uScrim.y*smoothstep(.28,.66,s.x)+uScrim.z*smoothstep(.34,.8,s.y)+uScrim.w*(1.-smoothstep(0.,.24,s.y));',
    '  col=mix(col,INK,clamp(sc,0.,.92));',
    '  vec2 v=s-.5;col*=1.-.38*pow(clamp(length(v*vec2(1.,.9))*1.3,0.,1.),2.6);',
    '  col+=(hash(p+.5)-.5)*.022;',
    '  gl_FragColor=vec4(clamp(col,0.,1.),1.);',
    '}'
  ].join('\n');

  function create(canvas, options) {
    options = options || {};
    var gl = null, isGL2 = false;
    // alpha: true although every pixel is opaque. An opaque full-screen canvas can be handed to Windows'
    // own compositor as an overlay, and some graphics drivers then leave scrambled pixels where the page
    // draws over it (the band under the site header). A canvas with alpha stays in Chrome's compositing.
    var attrs = { alpha: true, antialias: false, depth: false, stencil: false, premultipliedAlpha: false, preserveDrawingBuffer: false, powerPreference: 'high-performance' };
    try { gl = canvas.getContext('webgl2', attrs); isGL2 = !!gl; } catch (error) { gl = null; }
    if (!gl) { try { gl = canvas.getContext('webgl', attrs) || canvas.getContext('experimental-webgl', attrs); } catch (error) { gl = null; } }
    var api = { ok: false };
    if (!gl) return api;

    var program = gl.createProgram();
    var vs = shader(gl.VERTEX_SHADER, VERT), fs = shader(gl.FRAGMENT_SHADER, FRAG);
    gl.attachShader(program, vs); gl.attachShader(program, fs);
    gl.linkProgram(program);
    // Shaders compile off the main thread where the browser allows it: ask for the result only when ready.
    var parallel = gl.getExtension('KHR_parallel_shader_compile');
    var linked = false, failed = false;
    function checkLink() {
      if (linked || failed) return linked;
      if (parallel && !gl.getProgramParameter(program, parallel.COMPLETION_STATUS_KHR)) return false;
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        failed = true;
        if (window.console) console.warn('OneVine Mass: shader failed', gl.getProgramInfoLog(program), gl.getShaderInfoLog(fs));
        return false;
      }
      linked = true;
      setup();
      return true;
    }
    function shader(type, source) {
      var s = gl.createShader(type);
      gl.shaderSource(s, source);
      gl.compileShader(s);
      return s;
    }

    var loc = {}, buffer;
    function setup() {
      gl.useProgram(program);
      ['uRes', 'uPx', 'uTexA', 'uTexB', 'uCamA', 'uCamB', 'uHasA', 'uHasB', 'uAp', 'uFade', 'uMode', 'uLight', 'uRelief', 'uBlur', 'uDisc', 'uDiscLook', 'uKind',
        'uScrim', 'uTone', 'uSkyTop', 'uSkyMid', 'uSkyLow', 'uRoad', 'uRoadView'].forEach(function (name) { loc[name] = gl.getUniformLocation(program, name); });
      buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var a = gl.getAttribLocation(program, 'aPos');
      gl.enableVertexAttribArray(a);
      gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0);
      gl.uniform1i(loc.uTexA, 0);
      gl.uniform1i(loc.uTexB, 1);
      blank = makeTexture(null);
    }

    // Pictures: registered by key, fetched when first needed, at most a few kept on the card.
    var pictures = {}, blank = null, stamp = 0, maxResident = options.maxResident || 4;
    function makeTexture(image) {
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      if (image) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([11, 10, 9, 255]));
      var mip = image && isGL2;
      if (mip) gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, mip ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return t;
    }
    function picture(key, url, width, height) {
      if (!pictures[key]) pictures[key] = { url: url, w: width || 1, h: height || 1, tex: null, state: 'idle', shown: 0, used: 0 };
    }
    function need(key) {
      var pic = pictures[key];
      if (!pic) return null;
      pic.used = ++stamp;
      if (pic.state === 'idle') {
        pic.state = 'loading';
        var image = new Image();
        image.decoding = 'async';
        image.crossOrigin = 'anonymous';
        image.onload = function () {
          var place = function () {
            if (!linked) { window.setTimeout(place, 60); return; }
            evict();
            pic.tex = makeTexture(image);
            pic.w = image.naturalWidth; pic.h = image.naturalHeight;
            pic.state = 'ready';
            if (options.onPicture) options.onPicture(key);
          };
          if (image.decode) image.decode().then(place, place); else place();
        };
        image.onerror = function () { pic.state = 'failed'; };
        image.src = pic.url;
      }
      return pic;
    }
    function evict() {
      var ready = Object.keys(pictures).map(function (k) { return pictures[k]; }).filter(function (p) { return p.state === 'ready'; });
      if (ready.length < maxResident) return;
      ready.sort(function (a, b) { return a.used - b.used; });
      var old = ready[0];
      gl.deleteTexture(old.tex); old.tex = null; old.state = 'idle'; old.shown = 0;
    }

    // Camera: (cx, cy) is the point of the picture (0..1, y down) placed at (sx, sy) of the screen
    // (0..1); zoom 1 makes the picture just cover the screen, below 1 it floats in the dark.
    // The point slides so no empty edge shows while the picture is larger than the screen.
    function frame(pic, cam, width, height) {
      var aspect = pic.w / pic.h;
      var hp = Math.max(height, width / aspect) * cam.zoom, wp = hp * aspect;
      var left = cam.cx - cam.sx * width / wp, top = cam.cy - cam.sy * height / hp;
      var spanX = width / wp, spanY = height / hp;
      if (!cam.free) {
        if (spanX <= 1) left = Math.min(Math.max(left, 0), 1 - spanX);
        if (spanY <= 1) top = Math.min(Math.max(top, 0), 1 - spanY);
      }
      return { left: left, top: top, du: 1 / wp, dv: 1 / hp };
    }
    function project(key, cam, u, v) {
      var pic = pictures[key];
      if (!pic) return null;
      var f = frame(pic, cam, cssW, cssH);
      return { x: (u - f.left) / f.du, y: (v - f.top) / f.dv, scale: 1 / f.dv };
    }

    var cssW = 1, cssH = 1, ratio = 1;
    function resize(width, height, pixelRatio) {
      cssW = Math.max(1, width); cssH = Math.max(1, height); ratio = pixelRatio;
      var w = Math.round(cssW * ratio), h = Math.round(cssH * ratio);
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    }

    function bind(unit, pic) {
      gl.activeTexture(unit === 0 ? gl.TEXTURE0 : gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, pic && pic.tex ? pic.tex : blank);
    }
    function camUniform(name, pic, cam) {
      if (!pic || !cam) { gl.uniform4f(loc[name], 0, 0, 0, 0); return; }
      var f = frame(pic, cam, cssW, cssH);
      gl.uniform4f(loc[name], f.left, f.top, f.du / ratio, f.dv / ratio);
    }
    // Pictures fade in over a few frames once loaded, rather than popping.
    function shownOf(pic, dt) {
      if (!pic || pic.state !== 'ready') return 0;
      pic.shown = Math.min(1, pic.shown + dt / 280);
      return pic.shown;
    }

    var last = 0;
    function draw(state) {
      if (!checkLink()) return false;
      var now = performance.now(), dt = last ? Math.min(64, now - last) : 16;
      last = now;
      var r = ratio;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.useProgram(program);
      var a = state.a && need(state.a.key), b = state.b && need(state.b.key);
      var hasA = shownOf(a, dt), hasB = shownOf(b, dt);
      bind(0, a); bind(1, b);
      camUniform('uCamA', a, state.a); camUniform('uCamB', b, state.b);
      gl.uniform2f(loc.uRes, canvas.width, canvas.height);
      gl.uniform1f(loc.uPx, r);
      gl.uniform1f(loc.uHasA, hasA); gl.uniform1f(loc.uHasB, hasB);
      var ap = state.ap || { x: 0, y: 0, r: 0, rim: 0 };
      gl.uniform4f(loc.uAp, ap.x * r, ap.y * r, ap.r * r, ap.rim || 0);
      gl.uniform1f(loc.uFade, state.fade || 0);
      gl.uniform1f(loc.uMode, state.mode || 0);
      var light = state.light || { x: cssW / 2, y: cssH / 2, r: cssW, amb: 1, relief: 0 };
      gl.uniform4f(loc.uLight, light.x * r, light.y * r, light.r * r, light.amb);
      gl.uniform1f(loc.uRelief, light.relief || 0);
      gl.uniform1f(loc.uBlur, state.blur || 0);
      var d = state.disc || { x: 0, y: 0, r: 0, a: 0 };
      gl.uniform4f(loc.uDisc, d.x * r, d.y * r, d.r * r, d.a || 0);
      gl.uniform4f(loc.uDiscLook, 0, d.glow || 0, d.brk || 0, 0);
      var kw = d.kw || [0, 0, 1];
      gl.uniform3f(loc.uKind, kw[0], kw[1], kw[2]);
      var sc = state.scrim || [0, 0, 0, 0];
      gl.uniform4f(loc.uScrim, sc[0], sc[1], sc[2], sc[3]);
      gl.uniform2f(loc.uTone, state.expo == null ? 1 : state.expo, state.dim || 0);
      var sky = state.sky || { top: [0, 0, 0], mid: [0, 0, 0], low: [0, 0, 0] };
      gl.uniform3fv(loc.uSkyTop, sky.top); gl.uniform3fv(loc.uSkyMid, sky.mid); gl.uniform3fv(loc.uSkyLow, sky.low);
      var road = state.road || { hz: .6, travel: 0, night: 0, city: 0, vx: .5, bend: 0 };
      gl.uniform4f(loc.uRoad, road.hz, road.travel, road.night, road.city);
      gl.uniform2f(loc.uRoadView, road.vx, road.bend);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      // keep drawing while a picture is still fading in
      return (a && a.state === 'ready' && hasA < 1) || (b && b.state === 'ready' && hasB < 1);
    }

    api.ok = true;
    api.gl = gl;
    api.isGL2 = isGL2;
    api.picture = picture;
    api.need = need;
    api.ready = function (key) { return !!(pictures[key] && pictures[key].state === 'ready'); };
    api.linked = checkLink;
    api.failed = function () { return failed; };
    api.resize = resize;
    api.project = project;
    api.draw = draw;
    api.size = function () { return { w: cssW, h: cssH, ratio: ratio }; };
    return api;
  }

  window.OneVineMassGL = { create: create };
})();
